import { Heading, Tab, TabList, TabPanel, Tabs } from '@react-spectrum/s2';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useState } from 'react';
import type { Key } from 'react-aria-components';

import { LoadingInterstitial } from '@/components/LoadingInterstitial.tsx';
import { useBackToChat } from '@/hooks/useBackToChat';
import { useBusinessName } from '@/hooks/useBusinessName';
import { useUserSettings } from '@/hooks/useUserSettings';
import { SpaceLayout } from '@/layouts/SpaceLayout.tsx';
import { trackEvent } from '@/utils/fullstory';

import { ConnectedAccounts } from './ConnectedAccounts';
import { EmailSettings } from './EmailSettings';
import { MemoriesSettings } from './MemoriesSettings';
import { SignOutSection } from './SignOutSection';

const TABS = ['Integrations', 'Account'] as const;

export const SettingsSpace = memo(function SettingsSpace() {
	const { isLoading, error } = useUserSettings();
	const { businessName: businessNameValue } = useBusinessName();
	const [activeTab, setActiveTab] = useState<Key>('Integrations');
	const handleBack = useBackToChat();

	const businessName = businessNameValue ?? 'My Business';

	if (isLoading) {
		return (
			<SpaceLayout title="Settings" onBack={handleBack} fillHeight>
				<LoadingInterstitial />
			</SpaceLayout>
		);
	}

	if (error) {
		return (
			<div
				className="glass-panel flex h-full w-full items-center justify-center"
			>
				<p className="text-[--text-muted]">{error}</p>
			</div>
		);
	}

	return (
		<SpaceLayout title="Settings" onBack={handleBack} fillHeight>
			<Tabs
				selectedKey={activeTab}
				onSelectionChange={(key) => {
					trackEvent('Settings Tab Switched', { tab: String(key) });
					setActiveTab(key);
				}}
				aria-label="Settings"
				UNSAFE_className="flex min-h-0 flex-1 flex-col overflow-hidden"
			>
				<div className="flex-shrink-0 pb-3">
					<div className="flex flex-col gap-1 pb-6">
						<span className="text-sm text-gray-500">{businessName}</span>
						<Heading
							level={1}
							styles={style({
								font: 'heading',
								fontWeight: 'bold',
								fontSize: 'heading-lg',
							})}
						>
							Settings
						</Heading>
					</div>
					<TabList>
						{TABS.map((tab) => (
							<Tab key={tab} id={tab}>
								{tab}
							</Tab>
						))}
					</TabList>
				</div>
				<div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto pb-8">
					<TabPanel id="Integrations">
						<div className="mt-6">
							<ConnectedAccounts />
						</div>
					</TabPanel>
					<TabPanel id="Account">
						<div className="mt-6 flex flex-col gap-4">
							<MemoriesSettings />
							<EmailSettings />
							<SignOutSection />
						</div>
					</TabPanel>
				</div>
			</Tabs>
		</SpaceLayout>
	);
});
